import axios from "axios";
import html2canvas from "html2canvas";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import {
  getInfoSignatory,
  getSessionStorageValue,
} from "../../../service/getInfoSignatory";
import Design from "../../../svg/Design";
import Menu from "../../../svg/Menu";
import { formatCPF, formatPhone, getInitials } from "../../../utils/formats";
import { resizeSignCanvasToAspectRatio } from "../../../utils/resizeSignCanvasToAspectRatio";
import {
  isBirthDateValid,
  isDocumentValid,
  isEmailValid,
  isInitialValid,
  isNameValid,
  isPhoneValid,
  isSignValid,
} from "../../../utils/validations";
import LoadingSpinner from "../../LoadingSpinner";
import {
  Button,
  Container,
  ContainerButton,
  ContainerSection,
  Input,
  InputContainer,
  InputWrapper,
  Label,
  Option,
  OptionWrapper,
  Section,
  Wrapper,
  WrapperData,
} from "./styled";
import ToDesign from "./ToDesign";
import ToType from "./ToType";

interface SignatureProps {
  name: string | null;
  email: string | null;
  document: string | null;
  date_birth: string | null;
  phone: string | null;
}

export const Signature = () => {
  let initialFormData = {
    name: "",
    email: "",
    document: "",
    date_birth: "",
    phone: "",
  };
  const [formData, setFormData] = useState<SignatureProps | null>(
    initialFormData
  );
  const [errorName, setErrorName] = useState<boolean>(false);
  const [errorEmail, setErrorEmail] = useState<boolean>(false);
  const [errorDocument, setErrorDocument] = useState<boolean>(false);
  const [errorBirthDate, setErrorBirthDate] = useState<boolean>(false);
  const [errorPhone, setErrorPhone] = useState<boolean>(false);
  const [errorSign, setErrorSign] = useState<boolean>(false);
  const [errorInitial, setErrorInitial] = useState<boolean>(false);
  // const [errorSignUpload, setErrorSignUpload] = useState<boolean>(false);
  // const [errorInitialUpload, setErrorInitialUpload] = useState<boolean>(false);
  const [errorSignDesign, setErrorSignDesign] = useState<boolean>(false);
  const [errorInitialDesign, setErrorInitialDesign] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(
    "digitar"
  );
  const [signature, setSignature] = useState<string>("");
  const [initial, setInitial] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [signatureRequest, setSignatureRequest] = useState<any>();

  const urlID = window.location.search;
  const urlParams = new URLSearchParams(urlID);
  const id = urlParams.get("id");
  const url = process.env.REACT_APP_API_BASEURL;
  const pathname = process.env.REACT_APP_PATHNAME;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const data = await getInfoSignatory();
      if (data) {
        setFormData({
          name: data?.user.name,
          email: data?.user.email,
          document: data?.user.document,
          date_birth: data?.user.date_birth?.split("-").reverse().join("/"),
          phone: data?.user.phone,
        });
        setSignatureRequest(data.signature_request);
        setSignature(data?.user.name);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };
    if (getSessionStorageValue("token_signatory")) {
      setSignatureRequest(getSessionStorageValue("signature_request"));
      setFormData({
        email: getSessionStorageValue("user_email") || null,
        name: "",
        document: "",
        date_birth: "",
        phone: "",
      });
      setIsLoading(false);
    } else {
      fetchData();
    }

    return () => {
      sessionStorage.removeItem("token_signatory");
      sessionStorage.removeItem("user_email");
      sessionStorage.removeItem("signature_request");
    };
  }, []);

  useEffect(() => {
    const verifyName = formData?.name;
    const initialDefault = verifyName ? getInitials(verifyName) : "";
    setInitial(initialDefault);
    setSignature(verifyName || "");
  }, [formData?.name]);

  const handleSignatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorSign(false);
    setSignature(e.target.value);
  };

  const handleInitialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorInitial(false);
    setInitial(e.target.value);
  };

  const signatureRef = useRef<SignatureCanvas>(null);
  const initialRef = useRef<SignatureCanvas>(null);

  const handleOptionClick = (option: string) => {
    setSelectedOption(option === selectedOption ? null : option);
  };

  const handleInputChange = (field: string, value: string) => {
    if (formData) {
      setFormData({ ...formData, [field]: value });
    }

    if (field === "name") {
      setErrorName(!isNameValid(value));
    } else if (field === "email") {
      setErrorEmail(!isEmailValid(value));
    } else if (field === "document") {
      setErrorDocument(!isDocumentValid(value));
    } else if (field === "date_birth") {
      setErrorBirthDate(!isBirthDateValid(value));
    } else if (field === "phone") {
      setErrorPhone(!isPhoneValid(value));
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
  };

  // const {
  //   acceptedFiles: acceptedFilesSign,
  //   getRootProps: getRootPropsSign,
  //   getInputProps: getInputPropsSign,
  // } = useDropzone({
  //   accept: {
  //     "image/png": [".png"],
  //   },
  // });

  // const {
  //   acceptedFiles: acceptedFilesInitial,
  //   getRootProps: getRootPropsInitial,
  //   getInputProps: getInputPropsInitial,
  // } = useDropzone({
  //   accept: {
  //     "image/png": [".png"],
  //   },
  // });

  // const filesSign = acceptedFilesSign.map((file: any) => (
  //   <li key={file.path}>
  //     {file.path} - {file.size} bytes
  //   </li>
  // ));

  // const filesInitial = acceptedFilesInitial.map((file: any) => (
  //   <li key={file.path}>
  //     {file.path} - {file.size} bytes
  //   </li>
  // ));

  const handleSendClick = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    const validateAndSetError = (
      field: string,
      validationFunction: (field: string) => boolean,
      setErrorFunction: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      const isValid = validationFunction(field);
      setErrorFunction(!isValid);
      return isValid;
    };

    const isValidName = validateAndSetError(
      formData?.name ?? "",
      isNameValid,
      setErrorName
    );
    const isValidEmail = validateAndSetError(
      formData?.email ?? "",
      isEmailValid,
      setErrorEmail
    );
    const isValidDocument = validateAndSetError(
      formData?.document ?? "",
      isDocumentValid,
      setErrorDocument
    );
    const isValidBirthDate = validateAndSetError(
      formData?.date_birth ?? "",
      isBirthDateValid,
      setErrorBirthDate
    );
    const isValidPhone = validateAndSetError(
      formData?.phone ?? "",
      isPhoneValid,
      setErrorPhone
    );
    const isValidtest = () => {
      const signValid = isSignValid(signature);
      const initialValid = isInitialValid(initial);
      if (selectedOption === "digitar") {
        if (signValid && initialValid) {
          return true;
        } else if (!signValid && !initialValid) {
          setErrorSign(true);
          setErrorInitial(true);
        } else if (!signValid) {
          setErrorSign(true);
        } else if (!initialValid) {
          setErrorInitial(true);
        } else {
          return false;
        }
        // } else if (selectedOption === "upload") {
        //   if (filesSign.length === 0 && filesInitial.length === 0) {
        //     setErrorSignUpload(true);
        //     setErrorInitialUpload(true);
        //   } else if (filesSign.length === 0) {
        //     setErrorSignUpload(true);
        //   } else if (filesInitial.length === 0) {
        //     setErrorInitialUpload(true);
        //   } else {
        //     return true;
        //   }
      } else if (selectedOption === "desenhar") {
        if (signatureRef.current?.isEmpty() && initialRef.current?.isEmpty()) {
          setErrorInitialDesign(true);
          setErrorSignDesign(true);
        } else if (signatureRef.current?.isEmpty()) {
          setErrorSignDesign(true);
        } else if (initialRef.current?.isEmpty()) {
          setErrorInitialDesign(true);
        } else {
          setErrorSignDesign(false);
          setErrorInitialDesign(false);
          return true;
        }
      }
    };
    const validTest = isValidtest();
    if (
      isValidName &&
      isValidEmail &&
      isValidDocument &&
      isValidBirthDate &&
      isValidPhone &&
      validTest
    ) {
      let additionalData = {};

      if (selectedOption === "digitar") {
        const getElementSign = document.getElementById("preview-sign");
        const getElementInitial = document.getElementById("preview-initial");

        const canvasSign = await html2canvas(getElementSign!, {
          backgroundColor: null,
        });
        const capturedWidthSign = canvasSign.width;
        const resizedCanvasSign = resizeSignCanvasToAspectRatio(
          4 / 1,
          canvasSign,
          capturedWidthSign
        );
        const imageBase64Sign = resizedCanvasSign.toDataURL("image/png");

        const canvasInitial = await html2canvas(getElementInitial!, {
          backgroundColor: null,
        });
        const capturedWidthInitial = canvasInitial.width;
        const resizedCanvasInitial = resizeSignCanvasToAspectRatio(
          1 / 1,
          canvasInitial,
          capturedWidthInitial
        );
        const imageBase64Initial = resizedCanvasInitial.toDataURL("image/png");
        additionalData = {
          imageBase64Sign: imageBase64Sign,
          imageBase64Initial: imageBase64Initial,
        };
        // } else if (selectedOption === "upload") {
        //   additionalData = { filesSign, filesInitial };
      } else if (selectedOption === "desenhar") {
        const capturedWidthSign =
          signatureRef.current?.getTrimmedCanvas().width;
        const resizedCanvasSign = resizeSignCanvasToAspectRatio(
          4 / 1,
          signatureRef.current?.getTrimmedCanvas()!,
          capturedWidthSign!
        );
        const capturedWidthInitial =
          initialRef.current?.getTrimmedCanvas().width;
        const resizedCanvasInitial = resizeSignCanvasToAspectRatio(
          4 / 1,
          initialRef.current?.getTrimmedCanvas()!,
          capturedWidthInitial!
        );

        additionalData = {
          imageBase64Sign: resizedCanvasSign?.toDataURL("image/png"),
          imageBase64Initial: resizedCanvasInitial?.toDataURL("image/png"),
        };
      }

      try {
        setIsLoading(true);
        const tokenSignatory = getSessionStorageValue("token_signatory");
        const tokenUser = getSessionStorageValue("token_user");
        const token = tokenSignatory !== "" ? tokenSignatory : tokenUser;
        const response = await axios.post(
          `${url}/signatory?signature_request=${signatureRequest}`,
          JSON.stringify({
            ...formData,
            phone: formData?.phone?.replace(/\D/g, "") ?? "",
            date_birth:
              `${formData?.date_birth?.split("/").reverse().join("-")}` ?? "",
            ...additionalData,
          }),
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        resetForm();
        if (getSessionStorageValue("token_signatory")) {
          navigate(-1);
        } else {
          navigate(`${pathname}sign?id=${id}`);
        }
        sessionStorage.removeItem("token_signatory");
        sessionStorage.removeItem("user_email");
        sessionStorage.removeItem("signature_request");
        return response.data;
      } catch (error) {
        console.log("error", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <ContainerSection>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Section>
          <Container>
            <WrapperData>
              <h1>Informe Seus Dados</h1>
              <InputContainer>
                <InputWrapper>
                  <Label htmlFor="">Nome Completo</Label>
                  <Input
                    type="text"
                    value={formData?.name ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange("name", e.target.value)
                    }
                    placeholder="Digite o nome completo..."
                  />
                  {errorName && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: "-14px",
                        left: "8px",
                        color: "red",
                        fontSize: "12px",
                      }}
                    >
                      Nome inválido
                    </span>
                  )}
                </InputWrapper>
                <InputWrapper>
                  <Label htmlFor="">E-mail</Label>
                  <Input
                    type="text"
                    value={formData?.email ?? ""}
                    disabled
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange("email", e.target.value)
                    }
                    placeholder="Digite o e-mail..."
                  />
                  {errorEmail && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: "-14px",
                        left: "8px",
                        color: "red",
                        fontSize: "12px",
                      }}
                    >
                      E-mail inválido
                    </span>
                  )}
                </InputWrapper>
                <InputWrapper>
                  <Label htmlFor="">CPF</Label>
                  <Input
                    type="text"
                    maxLength={14}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formatCPF(formData?.document ?? "")}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange("document", e.target.value)
                    }
                    placeholder="Digite o CPF..."
                  />
                  {errorDocument && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: "-14px",
                        left: "8px",
                        color: "red",
                        fontSize: "12px",
                      }}
                    >
                      CPF inválido
                    </span>
                  )}
                </InputWrapper>
                <InputWrapper>
                  <Label htmlFor="">Data de Nascimento</Label>
                  <Input
                    type="date"
                    value={
                      formData?.date_birth?.split("/").reverse().join("-") ?? ""
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(
                        "date_birth",
                        e.target.value.split("-").reverse().join("/")
                      )
                    }
                    placeholder="Digite a data de nascimento..."
                  />
                  {errorBirthDate && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: "-14px",
                        left: "8px",
                        color: "red",
                        fontSize: "12px",
                      }}
                    >
                      Data inválida
                    </span>
                  )}
                </InputWrapper>
                <InputWrapper>
                  <Label htmlFor="">Telefone</Label>
                  <Input
                    type="text"
                    maxLength={15}
                    value={formatPhone(formData?.phone ?? "")}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange("phone", e.target.value)
                    }
                    placeholder="Digite o telefone..."
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  {errorPhone && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: "-14px",
                        left: "8px",
                        color: "red",
                        fontSize: "12px",
                      }}
                    >
                      Digite um telefone válido
                    </span>
                  )}
                </InputWrapper>
              </InputContainer>
            </WrapperData>
            <hr />
            <Wrapper>
              <h1>Criar Assinatura</h1>
              <OptionWrapper>
                <Option
                  left={true}
                  selected={selectedOption === "digitar"}
                  onClick={() => handleOptionClick("digitar")}
                >
                  <Menu
                    color={selectedOption === "digitar" ? "#fff" : "#355B94"}
                  />
                  <p>Padrão</p>
                </Option>
                {/* <Option
                selected={selectedOption === "upload"}
                onClick={() => handleOptionClick("upload")}
              >
                <Download
                  color={selectedOption === "upload" ? "#fff" : "#355B94"}
                />
                <p>Upload</p>
              </Option> */}
                <Option
                  selected={selectedOption === "desenhar"}
                  onClick={() => handleOptionClick("desenhar")}
                >
                  <Design
                    color={selectedOption === "desenhar" ? "#fff" : "#355B94"}
                  />
                  <p>Desenhar</p>
                </Option>
              </OptionWrapper>
              {selectedOption === "digitar" && (
                <ToType
                  signature={signature}
                  initial={initial}
                  handleInitialChange={handleInitialChange}
                  handleSignatureChange={handleSignatureChange}
                  errorSign={errorSign}
                  errorInitial={errorInitial}
                />
              )}
              {/* {selectedOption === "upload" && (
              <ToUpload
                filesSign={filesSign}
                filesInitial={filesInitial}
                getInputPropsSign={getInputPropsSign}
                getInputPropsInitial={getInputPropsInitial}
                getRootPropsSign={getRootPropsSign}
                getRootPropsInitial={getRootPropsInitial}
                errorInitialUpload={errorInitialUpload}
                errorSignatureUpload={errorSignUpload}
              />
            )} */}
              {selectedOption === "desenhar" && (
                <ToDesign
                  initialRef={initialRef}
                  signatureRef={signatureRef}
                  errorInitialDesign={errorInitialDesign}
                  errorSignatureDesign={errorSignDesign}
                />
              )}
            </Wrapper>
          </Container>
          <hr style={{ background: "#474747", width: "100%" }} />
          <ContainerButton>
            <Button
              background="#355B94"
              backgroundHover="#32568c"
              onClick={() => navigate(-1)}
            >
              Voltar
            </Button>
            <Button
              background="#77EC84"
              backgroundHover="#72E27F"
              color="#2c2c2c"
              onClick={handleSendClick}
            >
              Salvar
            </Button>
          </ContainerButton>
        </Section>
      )}
    </ContainerSection>
  );
};
